import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEO from '../SEO';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  MapPin, Phone, Mail, Clock, Star, TrendingUp, Award, Users,
  CheckCircle, ArrowRight, HelpCircle, Globe, Target, Bot, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const geoServices = [
  { id: "web-design", name: "Web Design", keywords: ["web design", "website design", "custom websites"] },
  { id: "seo", name: "SEO Services", keywords: ["SEO", "search engine optimization", "local SEO", "Google ranking"] },
  { id: "digital-marketing", name: "Digital Marketing", keywords: ["digital marketing", "online marketing", "PPC", "social media"] },
  { id: "ecommerce", name: "eCommerce", keywords: ["ecommerce", "online store", "Shopify", "WooCommerce"] },
  { id: "social-media", name: "Social Media Marketing", keywords: ["social media", "Facebook ads", "Instagram marketing"] },
  { id: "ppc", name: "PPC Advertising", keywords: ["PPC", "Google Ads", "pay per click", "SEM"] },
  { id: "reputation", name: "Reputation Management", keywords: ["reputation management", "review management", "online reviews"] },
];

function NationalCityLandingPage() {
  const { stateSlug, citySlug } = useParams();
  const [city, setCity] = useState(null);
  const [state, setState] = useState(null);
  const [otherCities, setOtherCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/geo/states/${stateSlug}/${citySlug}`);
        if (!res.ok) { setNotFound(true); setLoading(false); return; }
        const data = await res.json();
        setCity(data.city);
        setState(data.state);
        setOtherCities(data.other_cities || []);
      } catch {
        setNotFound(true);
      }
      setLoading(false);
    }
    fetchData();
  }, [stateSlug, citySlug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="city-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <noscript>
          <div style={{maxWidth:'900px',margin:'0 auto',padding:'20px',fontFamily:'system-ui,sans-serif'}}>
            <h1>{stateSlug?.replace('-',' ')} - {citySlug?.replace('-',' ')} Web Design & SEO Services</h1>
            <p>Professional web design and SEO services. <a href="/book-consultation">Get a free consultation</a> or call (817) 264-7476.</p>
            <p><a href={`/locations/${stateSlug}`}>View all cities in {stateSlug?.replace('-',' ')}</a></p>
          </div>
        </noscript>
      </div>
    );
  }

  if (notFound || !state || !city) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4" data-testid="not-found-heading">Location Not Found</h1>
          <p className="text-slate-600 mb-8">We couldn't find that location. Browse our service areas below.</p>
          <Link to="/locations"><Button data-testid="browse-locations-btn">Browse All Locations</Button></Link>
        </div>
      </div>
    );
  }

  const faqs = [
    {
      question: `How much does web design cost in ${city.name}, ${state.abbr}?`,
      answer: `Web design in ${city.name} typically ranges from $1,499 for basic websites to $15,000+ for complex eCommerce or enterprise sites. We offer transparent pricing with packages starting at $1,499 for ${city.name} businesses.`
    },
    {
      question: `How long does it take to build a website in ${city.name}?`,
      answer: `Most ${city.name} business websites take 2-4 weeks to complete. Simple sites can be done in 1-2 weeks, while complex eCommerce or custom projects may take 6-8 weeks.`
    },
    {
      question: `Do you offer SEO services for ${city.name} businesses?`,
      answer: `Yes! We specialize in local SEO for ${city.name}, ${state.abbr} businesses. Our SEO services help you rank higher in Google for searches like "web design ${city.name} ${state.abbr}" and "near me" queries.`
    },
    {
      question: `Can you help my ${city.name} business rank on Google Maps?`,
      answer: `Absolutely. Google Business Profile optimization is included in our SEO services. We help ${city.name} businesses appear in the local 3-pack and Google Maps results.`
    },
    {
      question: `Do you work with businesses outside of ${city.name}?`,
      answer: `Yes, we serve the entire ${state.name} area including ${(city.nearby || []).slice(0, 3).join(', ')}, and beyond. We also work with clients nationwide.`
    },
    {
      question: `What industries do you serve in ${city.name}?`,
      answer: `We work with all types of ${city.name} businesses including restaurants, dental practices, contractors, law firms, medical practices, retail stores, and more.`
    }
  ];

  const serviceCards = [
    {
      icon: <Globe className="h-12 w-12 text-blue-600" />,
      title: `${city.name} Web Design`,
      desc: `Custom, responsive websites designed specifically for ${city.name} businesses. Mobile-first approach with stunning visuals.`,
    },
    {
      icon: <TrendingUp className="h-12 w-12 text-blue-600" />,
      title: `${city.name} SEO Services`,
      desc: `Dominate ${city.name} search results. Local SEO strategies that put your business in front of ${city.name} customers.`,
    },
    {
      icon: <Target className="h-12 w-12 text-blue-600" />,
      title: `${city.name} Digital Marketing`,
      desc: `Comprehensive digital marketing solutions for ${city.name} businesses. PPC, social media, and content marketing.`,
    }
  ];

  const stats = [
    { number: "500+", label: "Businesses Served Nationwide" },
    { number: "97%", label: "Client Satisfaction Rate" },
    { number: "156%", label: "Avg. Traffic Increase" },
    { number: "4.9", label: "Google Rating" }
  ];

  const industries = [
    "Restaurants & Bars", "Auto Dealerships", "Healthcare & Dental", "Real Estate",
    "Contractors & Home Services", "Retail & Shopping", "Professional Services", "Fitness & Wellness"
  ];

  const localKeywords = city.seo_content?.longtail_keywords || [
    `web design ${city.name} ${state.abbr}`,
    `${city.name} web design`,
    `SEO ${city.name} ${state.abbr}`,
    `${city.name} SEO company`,
    `digital marketing ${city.name}`,
    `website development ${city.name} ${state.name}`,
    `${city.name} web developer`,
    `best web design ${city.name} ${state.abbr}`,
    `ecommerce ${city.name} ${state.abbr}`
  ];

  const pageUrl = `https://web-techservices.com/locations/${state.slug}/${city.slug}`;

  const schemaGraph = [
    {
      "@type": "LocalBusiness",
      "@id": `${pageUrl}#business`,
      "name": `Web-TechServices - ${city.name}, ${state.abbr}`,
      "url": pageUrl,
      "telephone": "(817) 264-7476",
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": city.name,
        "addressRegion": state.abbr,
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": city.lat,
        "longitude": city.lng
      },
      "areaServed": {
        "@type": "City",
        "name": city.name,
        "containedInPlace": { "@type": "State", "name": state.name }
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": `${city.name} Web Services`,
        "itemListElement": geoServices.map(s => ({
          "@type": "Offer",
          "itemOffered": { "@type": "Service", "name": `${city.name} ${s.name}` }
        }))
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "127",
        "bestRating": "5"
      }
    },
    {
      "@type": "WebPage",
      "@id": `${pageUrl}#webpage`,
      "url": pageUrl,
      "name": `${city.name} Web Design & SEO | Web-TechServices`,
      "description": `Premier web design and SEO services in ${city.name}, ${state.name}. Population: ${city.pop_formatted}. Serving ${city.county} County.${city.seo_content?.intro ? ' ' + city.seo_content.intro.slice(0, 200) : ''}`,
      "isPartOf": { "@id": "https://web-techservices.com/#website" },
      "breadcrumb": { "@id": `${pageUrl}#breadcrumb` }
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${pageUrl}#breadcrumb`,
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://web-techservices.com/" },
        { "@type": "ListItem", "position": 2, "name": "Locations", "item": "https://web-techservices.com/locations" },
        { "@type": "ListItem", "position": 3, "name": state.name, "item": `https://web-techservices.com/locations/${state.slug}` },
        { "@type": "ListItem", "position": 4, "name": `${city.name}, ${state.abbr}`, "item": pageUrl }
      ]
    },
    {
      "@type": "FAQPage",
      "mainEntity": faqs.map(f => ({
        "@type": "Question",
        "name": f.question,
        "acceptedAnswer": { "@type": "Answer", "text": f.answer }
      }))
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50" data-testid="national-city-page">
      <SEO
        title={`${city.name} Web Design & SEO Services | #1 ${city.name} ${state.abbr} Website Development`}
        description={`Premier web design and SEO services in ${city.name}, ${state.name}. Population ${city.pop_formatted}. ${city.seo_content?.intro ? city.seo_content.intro.slice(0, 120) + '...' : `Custom websites, local SEO, and digital marketing for ${city.name} businesses.`} Serving ${city.county} County. Free consultation. Call (817) 264-7476.`}
        keywords={localKeywords.join(', ')}
        canonical={pageUrl}
        schema={{ "@context": "https://schema.org", "@graph": schemaGraph }}
      />

      {/* Hero */}
      <section className="relative overflow-hidden py-24 px-4 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-800">
        <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZ2LTRoLTJ2NGgyem0tNiA2di00aC0ydjRoMnptMC02di00aC0ydjRoMnoiLz48L2c+PC9nPjwvc3ZnPg==')]" />
        <div className="relative max-w-6xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="h-6 w-6 text-blue-300" />
            <span className="text-blue-200 text-sm font-medium uppercase tracking-wider" data-testid="city-state-label">
              Serving {city.name}, {state.abbr} &bull; {city.county} County &bull; {state.region}
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white" data-testid="city-hero-heading">
            {city.name} Web Design & <br className="hidden md:block" />SEO Services
          </h1>
          <p className="text-xl text-white/90 mb-4 max-w-2xl">
            Transform your {city.name} business with custom web design, powerful SEO, and digital marketing strategies that drive real results.
          </p>
          <p className="text-blue-200 text-sm mb-8">
            Serving {city.name}'s {city.pop_formatted}+ residents and local businesses in {city.county} County, {state.name}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/consultation">
              <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50 font-bold px-8" data-testid="hero-consultation-btn">
                Free Consultation <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="tel:+18172647476">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold px-8">
                <Phone className="mr-2 h-5 w-5" /> (817) 264-7476
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-blue-600">{stat.number}</div>
                <div className="text-sm text-slate-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI-Generated Unique Content */}
      {city.seo_content && (
        <section className="max-w-4xl mx-auto px-4 py-20" data-testid="seo-content-section">
          {city.seo_content.intro && (
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6 text-slate-900">
                Web Design & Digital Marketing in {city.name}, {state.abbr}
              </h2>
              <div className="prose prose-lg prose-slate max-w-none">
                <p className="text-slate-700 leading-relaxed text-lg">{city.seo_content.intro}</p>
              </div>
            </div>
          )}
          {city.seo_content.market_overview && (
            <div className="mb-12 bg-white rounded-xl p-8 shadow-sm border border-slate-200">
              <h3 className="text-2xl font-bold mb-4 text-slate-900 flex items-center gap-3">
                <TrendingUp className="h-6 w-6 text-blue-600" />
                {city.name} Business & Market Overview
              </h3>
              <p className="text-slate-700 leading-relaxed">{city.seo_content.market_overview}</p>
            </div>
          )}
          {city.seo_content.why_digital_presence && (
            <div className="mb-12 bg-gradient-to-r from-blue-50 to-blue-50 rounded-xl p-8 border border-blue-100">
              <h3 className="text-2xl font-bold mb-4 text-slate-900 flex items-center gap-3">
                <Globe className="h-6 w-6 text-blue-600" />
                Why {city.name} Businesses Need a Strong Digital Presence
              </h3>
              <p className="text-slate-700 leading-relaxed">{city.seo_content.why_digital_presence}</p>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {city.seo_content.local_seo_strategy && (
              <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200">
                <h3 className="text-xl font-bold mb-4 text-slate-900 flex items-center gap-3">
                  <Target className="h-5 w-5 text-blue-600" />
                  Local SEO Strategy for {city.name}
                </h3>
                <p className="text-slate-700 leading-relaxed text-sm">{city.seo_content.local_seo_strategy}</p>
              </div>
            )}
            {city.seo_content.industries_served && (
              <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200">
                <h3 className="text-xl font-bold mb-4 text-slate-900 flex items-center gap-3">
                  <Users className="h-5 w-5 text-blue-600" />
                  Industries We Serve in {city.name}
                </h3>
                <p className="text-slate-700 leading-relaxed text-sm">{city.seo_content.industries_served}</p>
              </div>
            )}
          </div>
          {city.seo_content.services_detail && (
            <div className="mb-12 bg-white rounded-xl p-8 shadow-sm border border-slate-200">
              <h3 className="text-2xl font-bold mb-4 text-slate-900 flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-blue-600" />
                Complete Digital Services for {city.name} Businesses
              </h3>
              <p className="text-slate-700 leading-relaxed">{city.seo_content.services_detail}</p>
            </div>
          )}
          {city.seo_content.competitive_edge && (
            <div className="mb-12 bg-slate-900 text-white rounded-xl p-8">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-3">
                <Award className="h-5 w-5 text-amber-400" />
                Your Competitive Edge in {city.name}
              </h3>
              <p className="text-slate-300 leading-relaxed">{city.seo_content.competitive_edge}</p>
            </div>
          )}
          {city.seo_content.cta_paragraph && (
            <div className="text-center bg-gradient-to-r from-blue-600 to-blue-600 rounded-xl p-10 text-white">
              <p className="text-lg font-medium mb-6">{city.seo_content.cta_paragraph}</p>
              <Link to="/consultation">
                <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50 font-bold px-8">
                  Get Your Free {city.name} Consultation <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          )}
        </section>
      )}

      {/* Services */}
      <section className="max-w-6xl mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{city.name} Web Services</h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Comprehensive digital solutions tailored for {city.name}, {state.name} businesses
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {serviceCards.map((service, i) => (
            <Card key={i} className="hover:shadow-xl transition-shadow" data-testid={`service-card-${i}`}>
              <CardContent className="p-8">
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-slate-600 mb-6">{service.desc}</p>
                <Link to="/consultation">
                  <Button variant="outline" className="w-full">Get Started</Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-slate-900 text-white py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">
            Why {city.name} Businesses Choose Web-TechServices
          </h2>
          <p className="text-slate-300 text-center mb-12 max-w-2xl mx-auto">
            Our expertise combined with cutting-edge technology helps {city.name} businesses dominate their online presence.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: <MapPin className="h-8 w-8" />, color: "bg-blue-600", title: `${city.name} Market Experts`, desc: `Deep understanding of the ${city.name} and ${city.county} County market.` },
              { icon: <TrendingUp className="h-8 w-8" />, color: "bg-blue-600", title: "Proven SEO Results", desc: `Our clients dominate "near me" searches in ${city.name} and ${state.abbr}.` },
              { icon: <Bot className="h-8 w-8" />, color: "bg-blue-600", title: "AI-Powered Solutions", desc: `Cutting-edge AI chat, auto-blogging, and smart automation for ${city.name} businesses.` },
              { icon: <Award className="h-8 w-8" />, color: "bg-blue-600", title: "Award-Winning Design", desc: `Beautiful, conversion-focused websites for your ${city.name} business.` },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className={`w-16 h-16 ${item.color} rounded-full flex items-center justify-center mx-auto mb-4`}>{item.icon}</div>
                <h3 className="font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="max-w-6xl mx-auto px-4 py-20">
        <h2 className="text-3xl font-bold text-center mb-4">Industries We Serve in {city.name}</h2>
        <p className="text-center text-slate-600 mb-12">Specialized web solutions for {city.name}&apos;s diverse business community</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {industries.map((industry, i) => (
            <div key={i} className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <span className="font-medium text-slate-700">{industry}</span>
            </div>
          ))}
        </div>
      </section>

      {/* SEO Keywords Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-600 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Dominate {city.name}, {state.abbr} Search Results
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Get found when {city.name} customers search for your services. Our local SEO strategies help you rank for:
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {localKeywords.slice(0, 8).map((keyword, i) => (
              <span key={i} className="bg-white/20 px-4 py-2 rounded-full text-sm font-medium">&quot;{keyword}&quot;</span>
            ))}
          </div>
          <Link to="/#analyzer">
            <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50 font-bold">
              Check Your SEO Score Free
            </Button>
          </Link>
        </div>
      </section>

      {/* Nearby Cities & Info */}
      <section className="max-w-6xl mx-auto px-4 py-20">
        <h2 className="text-3xl font-bold text-center mb-4">Serving {city.name} & Surrounding Areas</h2>
        <p className="text-slate-600 text-center mb-12">We also serve these nearby communities in {state.name}</p>
        <div className="grid md:grid-cols-3 gap-8">
          <div>
            <h3 className="font-bold text-lg mb-4 text-blue-600">{city.name} Info</h3>
            <ul className="space-y-2 text-slate-600">
              <li>Population: {city.pop_formatted}</li>
              <li>County: {city.county} County</li>
              <li>State: {state.name} ({state.abbr})</li>
              <li>Region: {state.region}</li>
            </ul>
          </div>
          <div>
            <h3 className="font-bold text-lg mb-4 text-blue-600">Nearby Cities</h3>
            <ul className="space-y-2 text-slate-600">
              {(city.nearby || []).map((nearbyCity, i) => (
                <li key={i}>&#8226; {nearbyCity}</li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-bold text-lg mb-4 text-blue-600">Other {state.name} Cities</h3>
            <ul className="space-y-2 text-slate-600">
              {otherCities.slice(0, 8).map((c, i) => (
                <li key={i}>
                  <Link to={`/locations/${state.slug}/${c.slug}`} className="hover:text-blue-600 hover:underline">
                    &#8226; {c.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-4">
              <Link to={`/locations/${state.slug}`} className="text-blue-600 hover:underline text-sm" data-testid="view-all-state-cities">
                View All {state.name} Locations &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="max-w-4xl mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
          <p className="text-slate-600">Common questions about web design and SEO in {city.name}, {state.abbr}</p>
        </div>
        <div className="space-y-6">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-slate-200 p-6" data-testid={`faq-item-${i}`}>
              <h3 className="font-bold text-lg text-slate-900 mb-3 flex items-start gap-3">
                <HelpCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                {faq.question}
              </h3>
              <p className="text-slate-600 ml-9">{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-blue-700 to-blue-700 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Grow Your {city.name} Business?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Get a free website analysis and consultation. Let&apos;s discuss how we can help your {city.name} business succeed online.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/consultation">
              <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50 font-bold px-8" data-testid="cta-consultation-btn">
                Book Free Consultation
              </Button>
            </Link>
            <a href="tel:+18172647476">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-bold px-8">
                Call (817) 264-7476
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="bg-slate-100 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <Phone className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <h3 className="font-bold mb-1">Call Us</h3>
              <a href="tel:+18172647476" className="text-blue-600 hover:underline">(817) 264-7476</a>
            </div>
            <div>
              <Mail className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <h3 className="font-bold mb-1">Contact Us</h3>
              <Link to="/contact" className="text-blue-600 hover:underline">Send us a message</Link>
            </div>
            <div>
              <Clock className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <h3 className="font-bold mb-1">Business Hours</h3>
              <p className="text-slate-600">Mon-Fri: 9AM - 6PM CST</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default NationalCityLandingPage;
